/**
 * Sistema de Logging Estruturado
 * Responsável por logging centralizado com níveis e contexto
 */

export class Logger {
    constructor(debug = false, context = 'FingerprintSDK') {
        this.debugEnabled = debug;
        this.context = context;
        this.startTime = Date.now();
        this.logs = [];
        this.maxLogs = 1000; // Limite para evitar vazamento de memória
    }
    
    /**
     * Níveis de log disponíveis
     */
    static get LEVELS() {
        return {
            ERROR: 0,
            WARN: 1,
            INFO: 2,
            DEBUG: 3
        };
    }
    
    /**
     * Cores para console (se suportado)
     */
    static get COLORS() {
        return {
            ERROR: '#ff4444',
            WARN: '#ffaa00',
            INFO: '#0088ff',
            DEBUG: '#888888'
        };
    }
    
    /**
     * Log de erro
     */
    error(message, data = {}) {
        this.log('ERROR', message, data);
    }
    
    /**
     * Log de aviso
     */
    warn(message, data = {}) {
        this.log('WARN', message, data);
    }
    
    /**
     * Log de informação
     */
    info(message, data = {}) {
        this.log('INFO', message, data);
    }
    
    /**
     * Log de debug (apenas se debug estiver ativo)
     */
    debug(message, data = {}) {
        if (this.debugEnabled) {
            this.log('DEBUG', message, data);
        }
    }
    
    /**
     * Método principal de logging
     */
    log(level, message, data = {}) {
        const timestamp = new Date().toISOString();
        const relativeTime = Date.now() - this.startTime;
        
        const logEntry = {
            timestamp,
            relativeTime,
            level,
            context: this.context,
            message,
            data: this.sanitizeData(data)
        };
        
        // Armazena log interno
        this.storeLogs(logEntry);
        
        // Output para console se debug estiver ativo ou for erro/warn
        if (this.debugEnabled || level === 'ERROR' || level === 'WARN') {
            this.outputToConsole(logEntry);
        }
        
        // Envia para sistema externo se configurado
        this.sendToExternalSystem(logEntry);
    }
    
    /**
     * Sanitiza dados sensíveis antes do log
     */
    sanitizeData(data) {
        if (!data || typeof data !== 'object') {
            return data;
        }
        
        const sanitized = { ...data };
        const sensitiveKeys = ['password', 'token', 'key', 'secret', 'auth', 'credential'];
        
        const sanitizeObject = (obj) => {
            if (!obj || typeof obj !== 'object') return obj;
            
            const result = Array.isArray(obj) ? [] : {};
            
            for (const [key, value] of Object.entries(obj)) {
                const lowerKey = key.toLowerCase();
                
                if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
                    result[key] = '[REDACTED]';
                } else if (typeof value === 'object' && value !== null) {
                    result[key] = sanitizeObject(value);
                } else {
                    result[key] = value;
                }
            }
            
            return result;
        };
        
        return sanitizeObject(sanitized);
    }
    
    /**
     * Armazena logs internamente
     */
    storeLogs(logEntry) {
        this.logs.push(logEntry);
        
        // Remove logs antigos se exceder o limite
        if (this.logs.length > this.maxLogs) {
            this.logs = this.logs.slice(-this.maxLogs);
        }
    }
    
    /**
     * Output formatado para console
     */
    outputToConsole(logEntry) {
        const { timestamp, level, context, message, data, relativeTime } = logEntry;
        const color = Logger.COLORS[level];
        
        // Formato: [TIMESTAMP] [LEVEL] [CONTEXT] MESSAGE
        const prefix = `[${timestamp}] [${level}] [${context}] (+${relativeTime}ms)`;
        
        try {
            // Tenta usar console colorido se disponível
            if (console.groupCollapsed && Object.keys(data).length > 0) {
                console.groupCollapsed(
                    `%c${prefix} ${message}`,
                    `color: ${color}; font-weight: bold;`
                );
                console.table(data);
                console.groupEnd();
            } else {
                console.log(
                    `%c${prefix} ${message}`,
                    `color: ${color}; font-weight: bold;`,
                    Object.keys(data).length > 0 ? data : ''
                );
            }
        } catch (error) {
            // Fallback para console simples
            console.log(`${prefix} ${message}`, data);
        }
    }
    
    /**
     * Envia logs para sistema externo (implementação futura)
     */
    sendToExternalSystem(logEntry) {
        // Placeholder para integração com sistemas de logging externos
        // Como Sentry, LogRocket, etc.
        
        if (typeof window !== 'undefined' && window.fingerprintSDKLogHandler) {
            try {
                window.fingerprintSDKLogHandler(logEntry);
            } catch (error) {
                // Ignora erros do handler externo para não quebrar o SDK
            }
        }
    }
    
    /**
     * Cria logger filho com contexto específico
     */
    child(childContext) {
        const childLogger = new Logger(this.debugEnabled, `${this.context}:${childContext}`);
        childLogger.startTime = this.startTime; // Mantém tempo relativo
        return childLogger;
    }
    
    /**
     * Mede tempo de execução de uma função
     */
    async time(label, fn) {
        const start = performance.now();
        this.debug(`Iniciando ${label}`);
        
        try {
            const result = await fn();
            const duration = performance.now() - start;
            this.info(`${label} concluído`, { duration: `${duration.toFixed(2)}ms` });
            return result;
        } catch (error) {
            const duration = performance.now() - start;
            this.error(`${label} falhou`, { 
                duration: `${duration.toFixed(2)}ms`,
                error: error.message 
            });
            throw error;
        }
    }
    
    /**
     * Obtém logs armazenados
     */
    getLogs(level = null, limit = null) {
        let filteredLogs = this.logs;
        
        if (level) {
            const levelValue = Logger.LEVELS[level.toUpperCase()];
            filteredLogs = this.logs.filter(log => 
                Logger.LEVELS[log.level] <= levelValue
            );
        }
        
        if (limit) {
            filteredLogs = filteredLogs.slice(-limit);
        }
        
        return filteredLogs;
    }
    
    /**
     * Limpa logs armazenados
     */
    clearLogs() {
        this.logs = [];
        this.debug('Logs limpos');
    }
    
    /**
     * Obtém estatísticas de logs
     */
    getStats() {
        const stats = {
            total: this.logs.length,
            byLevel: {},
            timespan: {
                start: this.logs[0]?.timestamp,
                end: this.logs[this.logs.length - 1]?.timestamp
            }
        };
        
        // Conta logs por nível
        for (const level of Object.keys(Logger.LEVELS)) {
            stats.byLevel[level] = this.logs.filter(log => log.level === level).length;
        }
        
        return stats;
    }
    
    /**
     * Exporta logs como JSON
     */
    exportLogs() {
        return {
            context: this.context,
            exported_at: new Date().toISOString(),
            stats: this.getStats(),
            logs: this.logs
        };
    }
    
    /**
     * Configura handler externo para logs
     */
    static setExternalHandler(handler) {
        if (typeof window !== 'undefined') {
            window.fingerprintSDKLogHandler = handler;
        }
    }
    
    /**
     * Remove handler externo
     */
    static removeExternalHandler() {
        if (typeof window !== 'undefined') {
            delete window.fingerprintSDKLogHandler;
        }
    }
    
    /**
     * Ativa/desativa debug globalmente
     */
    setDebug(enabled) {
        this.debugEnabled = enabled;
        this.info(`Debug ${enabled ? 'ativado' : 'desativado'}`);
    }
    
    /**
     * Obtém configuração atual
     */
    getConfig() {
        return {
            debug: this.debugEnabled,
            context: this.context,
            maxLogs: this.maxLogs,
            currentLogs: this.logs.length
        };
    }
}